import { memo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, FileText, Send, CheckCircle2, Lock } from 'lucide-react';
import { useChatAI } from '@/src/hooks';
import { RAG_DEMO_PROMPT, CHAT_CONFIG } from '@/src/constants';
import { renderMarkdownBold } from '@/src/utils';
import { SectionHeader, Badge } from '@/src/components/ui';

function StarProduct() {
  const {
    messages,
    input,
    setInput,
    isLoading,
    messageCount,
    isLimitReached,
    sendMessage,
    handleKeyDown,
    scrollRef,
  } = useChatAI({
    systemPrompt: RAG_DEMO_PROMPT,
    maxMessages: CHAT_CONFIG.maxMessages,
    initialMessage: '¡Hola! He leído la **Guía_Casa_Vacacional.pdf**. Pregúntame lo que necesites sobre la casa.',
  });

  return (
    <section id="plataforma" className="relative py-20 bg-[#020202] overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-indigo-900/20 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <SectionHeader
          badge="Producto Estrella"
          title="Agente RAG Personalizado"
          subtitle="Prueba nuestro demo en vivo. Este agente ha 'leído' el manual de una casa vacacional y responde dudas específicas al instante."
        />

        {/* Demo Interface */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-5xl mx-auto bg-[#080808] rounded-3xl border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col md:flex-row h-[600px] relative group"
        >
          {/* Subtle Border Glow */}
          <div className="absolute -inset-[1px] bg-gradient-to-r from-indigo-500/30 via-purple-500/30 to-blue-500/30 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none z-0" />

          {/* Sidebar (Knowledge Base) */}
          <div className="w-full md:w-72 border-b md:border-b-0 md:border-r border-white/5 bg-[#050505] p-6 flex flex-col relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Base de Conocimiento
              </h4>
              <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse" />
            </div>

            <div className="space-y-3 flex-1">
              <div className="group flex items-center gap-3 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 cursor-default transition-all duration-200">
                <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate font-medium">
                    Guía_Casa_Vacacional.pdf
                  </div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-1">
                    <CheckCircle2 size={10} className="text-green-500" /> Indexado y Activo
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-6 border-t border-white/5">
              <div className="text-xs text-gray-500 mb-2 font-medium">Uso del Demo</div>
              <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    isLimitReached ? 'bg-red-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${(messageCount / CHAT_CONFIG.maxMessages) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                <span>Mensajes</span>
                <span>
                  {messageCount} / {CHAT_CONFIG.maxMessages}
                </span>
              </div>
            </div>
          </div>

          {/* Chat Interface */}
          <div className="flex-1 flex flex-col bg-[#080808] relative z-10">
            {/* Header Chat */}
            <div className="p-4 border-b border-white/5 flex items-center justify-between bg-[#050505]/50 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">IntelliGrow Agent</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-400">En línea</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat History */}
            <div
              ref={scrollRef}
              className="flex-1 p-6 space-y-6 overflow-y-auto scroll-smooth custom-scrollbar"
            >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-4'
                  }`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-1 shadow-[0_0_15px_rgba(99,102,241,0.3)]">
                      <Sparkles size={14} className="text-white" />
                    </div>
                  )}

                  <div
                    className={`max-w-[85%] px-5 py-4 rounded-2xl text-[15px] leading-relaxed shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-[#222] text-white rounded-tr-sm border border-white/5'
                        : 'bg-[#0f1016] border border-indigo-500/20 text-gray-200 rounded-tl-sm shadow-[0_4px_20px_rgba(0,0,0,0.2)]'
                    }`}
                  >
                    {renderMarkdownBold(msg.text)}
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {isLoading && (
                <div className="flex justify-start items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles size={14} className="text-white animate-pulse" />
                  </div>
                  <div className="bg-[#0f1016] border border-indigo-500/20 px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}

              {/* Limit Reached Notice */}
              {isLimitReached && (
                <div className="flex justify-center mt-4">
                  <Badge variant="error" icon={<Lock size={12} />}>
                    Límite de demo alcanzado. Contáctanos para ver más.
                  </Badge>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-5 border-t border-white/5 bg-[#050505]">
              <div className="relative group">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isLimitReached ? 'Límite de demo alcanzado' : '¿Cuál es la clave del WiFi?'}
                  disabled={isLoading || isLimitReached}
                  className="w-full bg-[#111] border border-white/10 rounded-xl pl-5 pr-14 py-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-inner disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={sendMessage}
                    aria-label="Send Message"
                    disabled={isLoading || !input.trim() || isLimitReached}
                    className="p-2 bg-indigo-600 rounded-lg text-white hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-900/50 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
              <div className="text-center mt-3 flex justify-center items-center gap-2">
                <p className="text-[10px] text-gray-600">
                  IntelliGrow AI Demo v2.4 • Powered by Gemini Flash
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default memo(StarProduct);
